import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const CATEGORY_FILTERS = [
  { id: null, name: 'General' },
  { id: 67, name: 'Shopping' },
  { id: 3, name: 'Entertainment' },
  { id: 5, name: 'Autos' },
  { id: 7, name: 'Beauty & Fitness' },
  { id: 10, name: 'Electronics' },
  { id: 12, name: 'Finance' },
  { id: 13, name: 'Food & Drink' },
  { id: 14, name: 'Games' },
  { id: 16, name: 'Health' },
  { id: 19, name: 'Home & Garden' },
  { id: 22, name: 'Jobs & Education' },
  { id: 71, name: 'Sports' },
  { id: 174, name: 'Travel' },
];

const DISCOVER_TIME_RANGES = [
  { value: '1d', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '12m', label: '12m' },
];

export default function GoogleTrends() {
  const [region, setRegion] = useState('TR');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Right side (labs/google-trends top section)
  const [keywordsInput, setKeywordsInput] = useState('');
  const [timeRange, setTimeRange] = useState('now 7-d');
  const [category, setCategory] = useState(0);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState('');
  const [renderKey, setRenderKey] = useState(0);
  const timeseriesRef = useRef(null);
  const relatedRef = useRef(null);

  // Category discover state
  const [activeCat, setActiveCat] = useState(null);
  const [discoverTime, setDiscoverTime] = useState('1d');
  const [discoverTop, setDiscoverTop] = useState([]);
  const [discoverRising, setDiscoverRising] = useState([]);
  const [discoverMeta, setDiscoverMeta] = useState(null);
  const [discoverFromCache, setDiscoverFromCache] = useState(false);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverErr, setDiscoverErr] = useState('');

  const fetchCategoryDiscover = useCallback(async (geo, catId, time, forceRefresh = false) => {
    setDiscoverLoading(true);
    setDiscoverErr('');
    setDiscoverMeta(null);
    setDiscoverFromCache(false);
    try {
      const nc = forceRefresh ? '&noCache=1' : '';
      const res = await fetch(`/api/google-trends/category-discover?geo=${encodeURIComponent(geo)}&category=${catId}&time=${encodeURIComponent(time)}${nc}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed');
      setDiscoverTop(data.top || []);
      setDiscoverRising(data.rising || []);
      setDiscoverMeta(data.meta || null);
      setDiscoverFromCache(!!data.fromCache);
    } catch (e) {
      setDiscoverErr(e?.message || 'Failed');
      setDiscoverTop([]);
      setDiscoverRising([]);
    } finally {
      setDiscoverLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeCat !== null) {
      fetchCategoryDiscover(region, activeCat, discoverTime);
    }
  }, [activeCat, region, discoverTime, fetchCategoryDiscover]);

  const now = useMemo(() => new Date(), []);
  const regions = [
    { code: 'TR', name: 'Turkey' },
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'AE', name: 'United Arab Emirates' },
  ];

  const timeRanges = [
    { value: 'now 1-d', label: 'Past 1 day' },
    { value: 'now 7-d', label: 'Past 7 days' },
    { value: 'today 1-m', label: 'Past 30 days' },
    { value: 'today 3-m', label: 'Past 90 days' },
    { value: 'today 12-m', label: 'Past 12 months' },
  ];

  async function fetchDailyTop() {
    setError('');
    setLoading(true);
    try {
      // No limit param → backend returns all for the day
      const res = await fetch(`/api/google-trends/international/daily/top?country=${encodeURIComponent(region)}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed to fetch');
      const normalized = Array.isArray(data.rows) ? data.rows.map(r => ({
        term: r.term?.value ?? r.term,
        total_score: r.total_score?.value ?? r.total_score,
        refresh_date: r.refresh_date?.value ?? r.refresh_date
      })) : [];
      setRows(normalized);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e?.message || 'Failed to fetch');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  // Trends embed helpers (ported from labs/google-trends)
  const loadTrendsScript = useCallback(async () => {
    setEmbedError('');
    if (typeof window !== 'undefined' && window.trends && window.trends.embed) return;
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-trends-embed="1"]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Trends embed script')));
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://ssl.gstatic.com/trends_nrtr/3351_RC01/embed_loader.js';
      s.async = true;
      s.defer = true;
      s.dataset.trendsEmbed = '1';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Trends embed script'));
      document.body.appendChild(s);
    });
  }, []);

  const doRenderWidgets = useCallback(async () => {
    setEmbedLoading(true);
    setEmbedError('');
    try {
      await loadTrendsScript();
      const kw = keywordsInput
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .slice(0, 5);
      if (kw.length === 0) {
        setEmbedError('Please enter at least one keyword.');
        setEmbedLoading(false);
        return;
      }
      const effectiveTime = timeRange === 'today 7-d' ? 'now 7-d' : timeRange;
      const comparisonItem = kw.map(k => ({ keyword: k, geo: region, time: effectiveTime }));
      const exploreObj = { comparisonItem, category: Number(category) || 0, property: '' };

      // Clear containers
      [timeseriesRef.current, relatedRef.current].forEach(node => { if (node) node.innerHTML = ''; });

      window.trends.embed.renderExploreWidgetTo(
        timeseriesRef.current,
        'TIMESERIES',
        exploreObj,
        { exploreQuery: null, guestPath: 'https://trends.google.com:443/trends/embed/' }
      );
      window.trends.embed.renderExploreWidgetTo(
        relatedRef.current,
        'RELATED_QUERIES',
        exploreObj,
        { exploreQuery: null, guestPath: 'https://trends.google.com:443/trends/embed/' }
      );
    } catch (e) {
      setEmbedError(e?.message || 'Failed to render Google Trends');
    } finally {
      setEmbedLoading(false);
    }
  }, [category, keywordsInput, loadTrendsScript, region, timeRange]);

  // Re-render widgets when renderKey increments to ensure fresh containers
  useEffect(() => {
    if (!keywordsInput.trim()) return;
    // Give React a tick to mount new keyed containers before rendering
    const id = setTimeout(() => { doRenderWidgets(); }, 0);
    return () => clearTimeout(id);
  }, [renderKey, doRenderWidgets, keywordsInput]);

  useEffect(() => {
    fetchDailyTop();
    if (keywordsInput.trim()) setRenderKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  useEffect(() => {
    // Initial render of widgets
    // stay empty until user searches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-2xl overflow-hidden border border-gray-200 shadow bg-white">
          <div className="p-6 bg-white text-gray-900 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/googletrendslogo.png" alt="Google Trends" className="w-auto object-contain" style={{ height: '2.1rem' }} />
            </div>
            <div className="flex items-center gap-4">
              <div className="text-xs text-gray-500">Last updated: {lastUpdated.toLocaleTimeString()}</div>
              <label className="text-xs text-gray-500">Region</label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/40"
              >
                {regions.map(r => (
                  <option key={r.code} value={r.code}>{r.name}</option>
                ))}
              </select>
              <button onClick={fetchDailyTop} disabled={loading} className="px-3 py-1.5 rounded-lg bg-black text-white text-xs disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
            </div>
          </div>

          {/* Category filter bar */}
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2 flex-wrap">
            {CATEGORY_FILTERS.map(c => (
              <button
                key={c.id ?? 'general'}
                onClick={() => setActiveCat(c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  activeCat === c.id
                    ? 'bg-black text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400 hover:text-gray-900'
                }`}
              >
                {c.name}
              </button>
            ))}
            {activeCat !== null && (
              <div className="ml-auto flex items-center gap-2">
                {DISCOVER_TIME_RANGES.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setDiscoverTime(t.value)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                      discoverTime === t.value
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Category discover results (shown when a category is selected) */}
          {activeCat !== null && (
            <div className="px-6 pt-4 pb-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {CATEGORY_FILTERS.find(c => c.id === activeCat)?.name} — Category Trends
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {discoverLoading
                      ? 'Fetching category trends…'
                      : discoverMeta
                        ? `${discoverMeta.success}/${discoverMeta.totalSeeds} seed OK → ${discoverTop.length} top + ${discoverRising.length} rising`
                        : 'No data'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {discoverFromCache && (
                    <span className="text-[10px] bg-blue-50 text-blue-500 px-2 py-0.5 rounded-full border border-blue-100">cached</span>
                  )}
                  {!discoverLoading && (
                    <button
                      onClick={() => fetchCategoryDiscover(region, activeCat, discoverTime, true)}
                      className="text-xs text-gray-500 hover:text-gray-900 px-2 py-1 rounded-md border border-gray-200 hover:border-gray-400 transition-colors"
                    >
                      ↻ Refresh
                    </button>
                  )}
                </div>
              </div>
              {discoverErr && <div className="text-xs text-red-500 mb-2">{discoverErr}</div>}
              {discoverLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin w-5 h-5 border-2 border-gray-300 border-t-gray-700 rounded-full mr-3" />
                  <span className="text-sm text-gray-500">Loading category data…</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Top Queries <span className="text-gray-400 font-normal">({discoverTop.length})</span>
                    </div>
                    <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                      <table className="min-w-full divide-y divide-gray-50">
                        <thead className="bg-gray-50/70 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Query</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-16">Score</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {discoverTop.map((q, i) => (
                            <tr key={i} className="hover:bg-gray-50/50">
                              <td className="px-4 py-1.5 text-xs text-gray-400">{i + 1}</td>
                              <td className="px-4 py-1.5 text-sm text-gray-900">{q.query}</td>
                              <td className="px-4 py-1.5 text-right text-sm text-gray-600">{q.value}</td>
                            </tr>
                          ))}
                          {discoverTop.length === 0 && (
                            <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-400">No data available</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Rising Queries <span className="text-gray-400 font-normal">({discoverRising.length})</span>
                    </div>
                    <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                      <table className="min-w-full divide-y divide-gray-50">
                        <thead className="bg-gray-50/70 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Query</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-24">Growth</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {discoverRising.map((q, i) => (
                            <tr key={i} className="hover:bg-gray-50/50">
                              <td className="px-4 py-1.5 text-xs text-gray-400">{i + 1}</td>
                              <td className="px-4 py-1.5 text-sm text-gray-900">{q.query}</td>
                              <td className="px-4 py-1.5 text-right text-sm font-semibold text-emerald-600">
                                {q.value === 'Breakout' ? 'Breakout' : `+${Number(q.value).toLocaleString()}%`}
                              </td>
                            </tr>
                          ))}
                          {discoverRising.length === 0 && (
                            <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-400">No data available</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Popular keywords table */}
              <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr className="bg-gray-100/70">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" style={{width:'60%'}}>Term</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" style={{width:'20%'}}>Total score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" style={{width:'20%'}}>Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {rows.map((r, idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50 hover:bg-gray-50/70">
                        <td className="px-4 py-2 text-sm text-gray-900">{r.term}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{r.total_score}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{r.refresh_date}</td>
                      </tr>
                    ))}
                    {rows.length === 0 && !loading && (
                      <tr><td className="px-4 py-4 text-sm text-gray-400" colSpan={3}>{error || 'No data'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Right: Labs top section (widgets) */}
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <input
                    value={keywordsInput}
                    onChange={(e) => setKeywordsInput(e.target.value)}
                    placeholder="Keywords (comma separated)"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64 bg-white focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/40"
                  />
                  <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/40">
                    {timeRanges.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <button onClick={() => setRenderKey(k => k + 1)} disabled={embedLoading} className="px-4 py-2 rounded-lg bg-black text-white text-sm disabled:opacity-50 shadow-sm">
                    {embedLoading ? 'Searching…' : 'Search'}
                  </button>
                </div>
                {embedError && <div className="mb-3 text-sm text-red-600">{embedError}</div>}
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm mb-3">
                  <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">Interest over time</div>
                  <div key={`ts-${renderKey}`} className="w-full min-h-[320px] overflow-hidden" ref={timeseriesRef} />
                </div>
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">Related queries</div>
                  <div key={`rel-${renderKey}`} className="w-full min-h-[320px] overflow-hidden" ref={relatedRef} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


